'use client';
import useTranslation from '../../context/Translation/useTranslation';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Image from '@repo/ui/src/components/Image';
import {
  AmplienceButtonProps,
  ArticleModuleProps,
  MediaType,
  MetaProps,
  SeoProps,
} from '@repo/ui/src/types';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { getButtonVariant } from '@repo/ui/src/components/Button/utils';

interface Props {
  _meta?: MetaProps;
  seo?: SeoProps;
  title?: string;
  SEO?: any;
  headerAsset?: Array<{
    image: {
      id: string;
      url: string;
    };
  }>;
  articleModules?: Array<ArticleModuleProps>;
  buttons?: Array<AmplienceButtonProps>;
  description?: string;
  backgroundColor?: string;
  backgroundImage?: MediaType;
  learnShopGrid?: boolean;
}

const getImage = (
  headerAsset: Props['headerAsset'],
  seoObj: Props['SEO'],
  articleModules: Props['articleModules'],
  backgroundImage: Props['backgroundImage'],
) => {
  if (headerAsset?.length) {
    return buildImageSrc(null, 600, 5, 4, headerAsset[0].image?.url);
  }
  if (seoObj?.ogImage) {
    return buildImageSrc(seoObj.ogImage, 600, 5, 4);
  }
  if (articleModules?.[0]?.image) {
    return buildImageSrc(articleModules[0].image, 600, 5, 4);
  }
  if (backgroundImage) {
    return buildImageSrc(backgroundImage, 800, 1, 1);
  }
  return buildImageSrc(null, 600, 5, 4);
};

const LearnCard = ({
  _meta,
  SEO,
  seo = SEO,
  title,
  headerAsset,
  articleModules,
  buttons,
  description,
  backgroundColor,
  backgroundImage,
  learnShopGrid,
}: Props) => {
  const background =
    backgroundColor === 'Dark' || backgroundColor === 'Black'
      ? 'tw-bg-neutral-700'
      : 'tw-bg-white tw-border tw-border-warm-300';
  const fontColor =
    backgroundColor === 'Dark' || backgroundColor === 'Black'
      ? 'tw-text-neutral-100 hover:tw-text-white'
      : 'tw-text-neutral-600 hover:tw-text-neutral-700';

  const dark = backgroundColor === 'Dark' || backgroundColor === 'Black';

  const { t } = useTranslation();
  const linkObj = _meta && buildLinkObj({ link: _meta?.deliveryKey });

  return (
    <div
      className={classNames('tw-h-full tw-grid tw-grid-rows-[auto_1fr_auto] tw-w-full', background)}
      role="group"
      aria-label="article"
    >
      {!learnShopGrid && (
        <Anchor
          link={linkObj?.href}
          className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-z-10"
          tabIndex={-1}
        />
      )}
      <div className="tw-relative tw-h-[250px]">
        <Image
          src={getImage(headerAsset, seo, articleModules, backgroundImage)}
          alt=""
          layout="fill"
          height={250}
          width={250}
          objectFit="cover"
        />
      </div>
      <div className="tw-pt-8 tw-px-5">
        {title && (
          <h4 className={classNames('tra-text-web-heading-6 tw-text-left tw-mb-2', fontColor)}>
            {title}
          </h4>
        )}
        {(seo?.ogDescription || description) && (
          <>
            <hr className="tw-border-2 tw-w-10 tw-border-brand-300 tw-my-4" />
            <p className={classNames('tw-line-clamp-8 md:tw-line-clamp-3', fontColor)}>
              {learnShopGrid ? description : seo.ogDescription}
            </p>
          </>
        )}
      </div>
      <div className="tw-p-5 tw-flex tw-gap-5">
        {learnShopGrid &&
          buttons &&
          buttons?.map((button, i) => (
            <Button
              {...button?.link}
              variant={getButtonVariant(button.style, button.icon)}
              as={Anchor}
              className="tw-z-20 tw-pr-6"
              key={i}
              dark={dark}
              size="small"
            >
              {button?.link?.displayName}
            </Button>
          ))}
        {!learnShopGrid && (
          <Button
            link={linkObj?.href}
            as={Anchor}
            variant={ButtonVariant.knife}
            className="tw-z-20"
            size="small"
          >
            {t('genericContent.learnMore', undefined, {
              default: 'Learn More',
            })}
          </Button>
        )}
      </div>
    </div>
  );
};

export default LearnCard;
